import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox, Space, Table, Tooltip } from 'antd';
import { CaretUpOutlined, CaretDownOutlined } from '@ant-design/icons';
import Column from '../../helpers/columns';
import { NULL_FILTER } from '../../helpers/api';
import useDatasource from '../../hooks/useDatasource';

import { getSaleReport, downloadAll } from '../../services/orders';
import { getInstitutions } from '../../services/taxonomies/institutions';
import { getUserTypesLight } from '../../services/taxonomies/user-types';
import PageTitle from '../PageTitle';
import ExportEntitiesButton from '../buttons/ExportEntitiesButton';
import useDictionaries from '../../hooks/useDictionaries';
import { COMMAND_TYPES, BULK_TYPES } from '../../pages/orders/constants';
import { getAdminUnits } from '../../services/taxonomies/zonal-restriction';

const dictionaries = {
  userTypes: getUserTypesLight,
  institutions: getInstitutions,
};

const BulkType = {
  content: Object.values(BULK_TYPES).map((item) => ({ id: item })),
  loading: false,
  error: false,
};
const ActivationType = {
  content: [{ id: '0' }, { id: '1' }, { id: '2' }],
  loading: false,
  error: false,
};
const commandType = {
  content: COMMAND_TYPES,
  loading: false,
  error: false,
};

const SaleReportOrdersGrid = () => {
  const { t } = useTranslation();

  const [{ institutions, userTypes }] = useDictionaries(dictionaries);

  const {
    pagination,
    sort,
    criterias,
    content,
    handleChange,
    handleSortChange,
  } = useDatasource(getSaleReport);

  useEffect(() => {
    if (content?.[0]) {
      localStorage.setItem('julianStartDate', content[0].julianStartDate);
    }
  }, [content]);

  const [uatsDictionary, setUatsDictionary] = useState({
    content: [],
    loading: false,
    error: false,
  });

  const metropolitanEnabled = useMemo(
    () => window._env_.METROPOLITAN_ENABLE === 'true',
    [],
  );

  useEffect(() => {
    let cancelled = false;

    if (!metropolitanEnabled) {
      setUatsDictionary({
        content: [],
        loading: false,
        error: false,
      });

      return () => {
        cancelled = true;
      };
    }

    setUatsDictionary({
      content: [],
      loading: true,
      error: false,
    });

    setUatsDictionary({
      content: [],
      loading: true,
      error: false,
    });

    const loadUats = async () => {
      try {
        const data = await getAdminUnits();
        if (!cancelled) {
          setUatsDictionary({
            content: data,
            loading: false,
            error: false,
          });
        }
      } catch (e) {
        if (!cancelled) {
          setUatsDictionary({
            content: [],
            loading: false,
            error: true,
          });
        }
      }
    };

    loadUats();

    return () => {
      cancelled = true;
    };
  }, [metropolitanEnabled]);

  const columns = useMemo(() => {
    return [
      Column.text('orderId', t('components.salesReport.orderId'), {
        filter: true,
      }),
      Column.text('origProductId', t('components.salesReport.origProductId'), {
        filter: true,
      }),
      Column.text(
        'origProductName',
        t('components.salesReport.origProductName'),
        { filter: true },
      ),
      Column.text('cardLogicalId', t('components.salesReport.cardLogicalId'), {
        filter: true,
      }),
      Column.text('userFullname', t('components.salesReport.userFullname'), {
        filter: true,
        sort: false,
      }),
      Column.text(
        'userIdentityCard',
        t('components.salesReport.userIdentityCard'),
        {
          filter: true,
          sort: false,
        },
      ),
      Column.text('userId', t('components.salesReport.userId'), {
        filter: true,
      }),
      Column.date(
        'orderCreatedAt',
        t('components.salesReport.orderCreatedAt'),
        {
          filter: true,
        },
      ),
      Column.date(
        'orderUpdatedAt',
        t('components.salesReport.orderUpdatedAt'),
        {
          filter: true,
        },
      ),
      Column.date('startDate', t('components.salesReport.startDate'), {
        filter: true,
      }),
      Column.text('dateStatusName', t('components.salesReport.statusDate'), {
        filter: true,
        sort: false,
        width: 150,
      }),
      Column.dictionary(
        'dateStatus',
        t('components.salesReport.statusDate'),
        ActivationType,
        {
          width: 150,
          filter: true,
          dictLabel: (entry) =>
            t(`components.salesReport.dateStatus.${entry.id}`),
        },
      ),
      Column.date(
        'startValidPeriod',
        t('components.salesReport.startValidPeriod'),
        {
          sort: false,
        },
      ),
      Column.date(
        'endValidPeriod',
        t('components.salesReport.endValidPeriod'),
        {
          sort: false,
        },
      ),
      Column.text('metaData1', t('components.salesReport.metaData1'), {
        filter: true,
      }),
      Column.text('metaData2', t('components.salesReport.metaData2'), {
        filter: true,
      }),
      Column.dictionary(
        'institution',
        t('entity.admin.user.institutionId'),
        institutions,
        {
          width: 150,
          filter: true,
          dictKey: 'name',
        },
      ),
      Column.text('subdivision', t('entity.admin.user.subdivisionId'), {
        width: 150,
        filter: true,
      }),
      Column.dictionary(
        'userType',
        t('entity.admin.user.userType'),
        userTypes,
        {
          width: 150,
          filter: true,
          dictKey: 'name',
        },
      ),
      Column.text('parentName', t('components.salesReport.parentId'), {
        sort: false,
        filter: true,
      }),
      Column.text(
        'employeeNumber',
        t('components.salesReport.employeeNumber'),
        {
          filter: true,
        },
      ),
      Column.dictionary('bulkType', t('entity.orders.bulkTypes'), BulkType, {
        width: 150,
        filter: true,
        dictLabel: (entry) => t(`entity.orders.bulkType.${entry.id}`),
      }),
      Column.dictionary(
        'commandType',
        t('entity.orders.command'),
        commandType,
        {
          width: 150,
          filter: true,
          dictLabel: (entry) => t(`entity.orders.commandType.${entry.id}`),
        },
      ),
      Column.text('productQty', t('components.salesReport.productQty'), {
        filter: true,
      }),
      Column.text(
        'productUnitPrice',
        t('components.salesReport.productUnitPrice'),
        {
          filter: true,
        },
      ),
      Column.text('lineGroups', t('components.salesReport.lineGroups'), {
        filter: true,
      }),
      Column.text('lines', t('components.salesReport.lines'), { filter: true }),
      ...(metropolitanEnabled
        ? [
            Column.dictionary(
              'uatStart',
              t('components.salesReport.uatStart'),
              uatsDictionary,
              {
                filter: true,
              },
            ),
            Column.dictionary(
              'uatStop',
              t('components.salesReport.uatStop'),
              uatsDictionary,
              {
                filter: true,
              },
            ),
          ]
        : []),
    ];
  }, [t, institutions, userTypes, metropolitanEnabled, uatsDictionary]);

  const withSortControls = useCallback(
    (cols) =>
      cols.map((col) => {
        if (col.sorter !== true) return col;

        const columnKey = col.key;
        const isAsc = sort && sort[0] === columnKey && sort[1] === 'asc';
        const isDesc = sort && sort[0] === columnKey && sort[1] === 'desc';

        const inactiveColor = '#bfbfbf';
        const activeColor = '#1890ff';

        return {
          ...col,
          sorter: false,
          title: (
            <div style={{ display: 'inline-flex', alignItems: 'center' }}>
              <span>{col.title}</span>
              <span
                style={{
                  display: 'inline-flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  marginLeft: 4,
                  fontSize: 11,
                }}
              >
                <Tooltip
                  title={
                    isAsc
                      ? t('common.cancelSort', 'Cancel sort')
                      : t('common.sortAsc', 'Sort ascending')
                  }
                >
                  <CaretUpOutlined
                    role="button"
                    tabIndex={0}
                    style={{
                      color: isAsc ? activeColor : inactiveColor,
                      cursor: 'pointer',
                      marginBottom: -3,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSortChange(columnKey, isAsc ? null : 'asc');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSortChange(columnKey, isAsc ? null : 'asc');
                      }
                    }}
                  />
                </Tooltip>
                <Tooltip
                  title={
                    isDesc
                      ? t('common.cancelSort', 'Cancel sort')
                      : t('common.sortDesc', 'Sort descending')
                  }
                >
                  <CaretDownOutlined
                    role="button"
                    tabIndex={0}
                    style={{
                      color: isDesc ? activeColor : inactiveColor,
                      cursor: 'pointer',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSortChange(columnKey, isDesc ? null : 'desc');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSortChange(columnKey, isDesc ? null : 'desc');
                      }
                    }}
                  />
                </Tooltip>
              </span>
            </div>
          ),
        };
      }),
    [sort, t, handleSortChange],
  );

  const withNullFilter = useCallback(
    (cols) =>
      cols.map((col) => {
        if (!col.filterDropdown) return col;

        const originalFilterDropdown = col.filterDropdown;

        return {
          ...col,
          filterDropdown: (props) => {
            const { setSelectedKeys, selectedKeys, confirm, clearFilters } =
              props;
            const isNullActive =
              selectedKeys?.length === 1 && selectedKeys[0] === NULL_FILTER;

            const handleCheckboxChange = (e) => {
              if (e.target.checked) {
                setSelectedKeys([NULL_FILTER]);
              } else {
                setSelectedKeys([]);
              }
            };

            const handleReset = () => {
              clearFilters();
              confirm();
            };

            const interceptInteraction = (e) => {
              if (isNullActive) {
                if (e.target.closest('button')) {
                  return;
                }
                e.stopPropagation();
                e.preventDefault();
              }
            };

            return (
              <div style={{ padding: 8 }}>
                <div
                  style={{
                    marginBottom: 8,
                  }}
                  onClickCapture={interceptInteraction}
                  onMouseDownCapture={interceptInteraction}
                  onKeyDownCapture={interceptInteraction}
                >
                  {originalFilterDropdown({
                    ...props,

                    selectedKeys: isNullActive ? [] : selectedKeys,
                    clearFilters: handleReset,
                  })}
                </div>

                <div
                  style={{
                    padding: '10px 8px 4px',
                    borderTop: '1px solid #f0f0f0',
                  }}
                >
                  <Checkbox
                    checked={isNullActive}
                    onChange={handleCheckboxChange}
                  >
                    {t('common.nullOnly')}
                  </Checkbox>
                </div>
              </div>
            );
          },
        };
      }),
    [t],
  );

  const filteredColumnsForTable = useMemo(
    () =>
      withNullFilter(
        withSortControls(
          columns.filter((column) => column.key !== 'dateStatusName'),
        ),
      ),
    [columns, withSortControls, withNullFilter],
  );

  const filteredColumnsForCSV = useMemo(
    () => columns.filter((column) => column.key !== 'dateStatus'),
    [columns],
  );

  return (
    <>
      <PageTitle>
        <Space>
          <ExportEntitiesButton
            columns={filteredColumnsForCSV}
            data={{
              sort,
              criterias,
            }}
            downloadFiles={downloadAll}
          />
        </Space>
      </PageTitle>
      <Table
        rowKey={(record, index) => index}
        columns={filteredColumnsForTable}
        // loading={loading}
        pagination={pagination}
        dataSource={content}
        onChange={handleChange}
        scroll={{ x: 700, y: 700 }}
        size="small"
      />
    </>
  );
};

export default SaleReportOrdersGrid;
